import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { SkeletonSurveyParticipant } from '../../../features/skeleton'
import { CheckCircle, Lock } from 'lucide-react'
import { SurveyService, type SurveyParticipantData } from '../services'
import { type SurveyAnswer } from '../components/questions'
import { SurveyParticipantStateView } from './SurveyParticipantStateView'
import { SurveyParticipantForm } from './SurveyParticipantForm'
import { isAnswered } from './surveyParticipantUtils'
import { CenterBox, ErrorMessage } from './SurveyParticipantPageStyles'
import { analytics } from '../../../utils/analytics'

export function SurveyParticipantPage() {
  const { nodeId } = useParams<{ nodeId: string }>()
  const [data, setData] = useState<SurveyParticipantData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [answers, setAnswers] = useState<Record<number, SurveyAnswer>>({})
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(false)
  const startTimeRef = useRef<number>(Date.now())

  useEffect(() => {
    if (!nodeId) {
      setError(true)
      setLoading(false)
      return
    }

    SurveyService.getPublicSurvey(nodeId)
      .then((res) => {
        if (res.error || !res.data) {
          setError(true)
        } else {
          setData(res.data)
          startTimeRef.current = Date.now()
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [nodeId])

  const handleChange = useCallback((index: number, value: SurveyAnswer) => {
    setAnswers((prev) => ({ ...prev, [index]: value }))
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!nodeId || !data) return
    setSubmitting(true)
    setSubmitError(false)

    const orderedAnswers = data.surveyQuestions.map((_, i) => answers[i] ?? null)
    const durationSeconds = Math.round((Date.now() - startTimeRef.current) / 1000)

    const res = await SurveyService.submitAnswers(nodeId, orderedAnswers, durationSeconds)

    if (res.error) {
      // Survey was closed between page load and submit — flip to closed view.
      const code = (res.error as { code?: string } | null)?.code
      if (code === 'SURVEY_CLOSED') {
        setData((prev) => (prev ? { ...prev, status: 'closed' } : prev))
        setSubmitting(false)
        return
      }
      setSubmitError(true)
      setSubmitting(false)
      return
    }

    analytics.surveyResponseSubmitted({
      node_id: nodeId,
      duration_seconds: durationSeconds,
      question_count: data.surveyQuestions.length,
    })
    setSubmitted(true)
  }, [nodeId, data, answers])

  const answeredCount = useMemo(() => Object.values(answers).filter(isAnswered).length, [answers])

  if (loading) return <SkeletonSurveyParticipant />

  if (error || !data) {
    return (
      <CenterBox>
        <ErrorMessage>Survey not found.</ErrorMessage>
      </CenterBox>
    )
  }

  if (data.status === 'closed') {
    return (
      <SurveyParticipantStateView
        icon={Lock}
        title="This survey is closed"
        body="Thanks for your interest — we're no longer accepting new responses."
      />
    )
  }

  if (submitted) {
    return (
      <SurveyParticipantStateView
        icon={CheckCircle}
        title="Thank you for your responses"
        body="Your answers help validate real product decisions. This feedback makes a difference."
      />
    )
  }

  return (
    <SurveyParticipantForm
      questions={data.surveyQuestions}
      answers={answers}
      answeredCount={answeredCount}
      submitting={submitting}
      submitError={submitError}
      onChange={handleChange}
      onSubmit={handleSubmit}
    />
  )
}
